'use client';

import { forwardRef, useCallback, useState, useRef } from 'react';
import classnames from 'classnames';
import Box from '../Box/Box';
import Typography from '../Typography/Typography';
import Iconography from '../Iconography/Iconography';
import IconButton from '../IconButton/IconButton';
import ProgressBar from '../ProgressBar/ProgressBar';
import styles from './FileUpload.module.scss';
import {
  FileUploadStatus,
  UploadedFile,
  UploadUrlResponse,
  UploadError,
  DEFAULT_MAX_RETRIES,
  DEFAULT_RETRY_DELAY_MS,
  DEFAULT_TIMEOUT_MS,
  DEFAULT_MAX_FILE_SIZE,
  DEFAULT_ACCEPTED_TYPES,
  classifyError,
  getRetryDelay,
  createTimeoutPromise,
  sleep,
  createXhrUpload,
} from './FileUpload.utils';

type Theme = 'dark' | 'light';

type ValidationResponse = {
  validationType?: 'success' | 'warning' | 'error';
  message?: string;
};

export type FileValidationErrorType = 'FILE_TOO_LARGE' | 'INVALID_FILE_TYPE';

export type FileValidationError = {
  type: FileValidationErrorType;
  file: File;
  maxFileSize?: number;
  acceptedTypes?: string;
};

export type FileUploadProps = {
  /** Test ID for the component */
  dataTestId: string;
  /** Theme variant @default 'dark' */
  theme?: Theme;
  /** Accepted file types (e.g., '.pdf,.jpg,.png,.log') @default '.pdf,.jpg,.png,.log' */
  accept?: string;
  /** Maximum file size in bytes @default 10485760 (10MB) */
  maxFileSize?: number;
  /** Allow multiple file uploads @default false */
  multiple?: boolean;
  /** Label text above the component @default 'Attachments' */
  label?: string;
  /** Helper text shown below the label @default 'PDF, JPG, PNG, LOG files supported (max 10 MB each)' */
  helperText?: string;
  /** Text shown inside the drop zone @default 'Drag and drop files here, or click to select' */
  dropzoneText?: string;
  /** Function to get the signed upload URL from backend */
  getUploadUrl: (file: File) => Promise<UploadUrlResponse>;
  /** Optional callback when upload completes */
  onUploadComplete?: (fileUrl: string, file: File) => void;
  /** Optional callback when upload fails */
  onUploadError?: (error: Error, file: File) => void;
  /** Optional callback when file is removed (receives fileId if file was uploaded) */
  onFileRemove?: (fileId?: string) => void;
  /** Additional CSS class */
  className?: string;
  /** Disabled state @default false */
  disabled?: boolean;
  /** Maximum number of retry attempts @default 3 */
  maxRetries?: number;
  /** Base delay in milliseconds for retry backoff @default 1000 */
  retryDelayMs?: number;
  /** Upload timeout in milliseconds @default 60000 (60 seconds) */
  timeoutMs?: number;
  /** Optional callback when retry is attempted */
  onRetry?: (file: File, retryCount: number, error: UploadError) => void;
  /** Whether the field is required */
  required?: boolean;
  /** Validation response object containing error/warning/success state and message */
  validationResponse?: ValidationResponse;
  /** Whether to show the validation message area @default true */
  showValidation?: boolean;
  /**
   * Optional callback to customize file validation error messages.
   * Return a custom message string, or undefined to use the default message.
   */
  onValidationError?: (error: FileValidationError) => string | undefined;
  /** Show API response section for development reference @default false */
  showDevResponse?: boolean;
};

/**
 * FileUpload component with drag and drop functionality
 *
 * @component
 * @example
 * ```tsx
 * <FileUpload
 *   dataTestId="case-attachments"
 *   theme="light"
 *   getUploadUrl={async (file) => {
 *     const response = await api.post('/upload-url', { filename: file.name });
 *     return response.data;
 *   }}
 *   onUploadComplete={(url) => console.log('Uploaded:', url)}
 * />
 * ```
 */
const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(
  (
    {
      dataTestId,
      theme = 'dark',
      accept = DEFAULT_ACCEPTED_TYPES,
      maxFileSize = DEFAULT_MAX_FILE_SIZE,
      multiple = false,
      label = 'Attachments',
      helperText = 'PDF, JPG, PNG, LOG files supported (max 10 MB each)',
      dropzoneText = 'Drag and drop files here, or click to select',
      getUploadUrl,
      onUploadComplete,
      onUploadError,
      onFileRemove,
      className,
      disabled = false,
      maxRetries = DEFAULT_MAX_RETRIES,
      retryDelayMs = DEFAULT_RETRY_DELAY_MS,
      timeoutMs = DEFAULT_TIMEOUT_MS,
      onRetry,
      required = false,
      validationResponse,
      showValidation = true,
      onValidationError,
      showDevResponse = false,
    },
    ref
  ) => {
    const [files, setFiles] = useState<UploadedFile[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const abortControllersRef = useRef<Map<number, AbortController>>(new Map());
    // Store API responses for dev reference
    const [apiResponses, setApiResponses] = useState<Record<string, unknown>[]>([]);
    const uploadedFileIdsRef = useRef<string[]>([]);

    // ========================================
    // File State Management
    // ========================================

    const updateFileState = useCallback((index: number, updates: Partial<UploadedFile>) => {
      setFiles((prev) => prev.map((f, i) => (i === index ? { ...f, ...updates } : f)));
    }, []);

    // ========================================
    // Upload Logic
    // ========================================

    const uploadFile = useCallback(
      async (file: File, index: number, retryCount: number = 0) => {
        const abortController = new AbortController();
        abortControllersRef.current.set(index, abortController);

        try {
          // Update status
          updateFileState(index, {
            status: retryCount > 0 ? 'retrying' : 'uploading',
            progress: 0,
            retryCount,
            error:
              retryCount > 0
                ? `Retrying... (attempt ${retryCount + 1}/${maxRetries + 1})`
                : undefined,
          });

          // Get signed URL with timeout
          let uploadUrl: string;
          let fileUrl: string;
          let tenantId: string | undefined;
          let devResponse: Record<string, unknown> | undefined;

          try {
            const urlResponse = await Promise.race([
              getUploadUrl(file),
              createTimeoutPromise(timeoutMs),
            ]);
            uploadUrl = urlResponse.uploadUrl;
            fileUrl = urlResponse.fileUrl;
            tenantId = urlResponse.tenantId;
            devResponse = urlResponse.devResponse;

            // Store dev response if provided and showDevResponse is enabled
            if (showDevResponse && devResponse && urlResponse.fileId) {
              uploadedFileIdsRef.current = [...uploadedFileIdsRef.current, urlResponse.fileId];
              setApiResponses((prev) => [...prev, devResponse as Record<string, unknown>]);
            }
          } catch (urlError) {
            const classifiedError = classifyError(urlError);
            throw Object.assign(new Error(classifiedError.message), {
              uploadError: classifiedError,
            });
          }

          // Upload to S3
          await createXhrUpload({
            uploadUrl,
            file,
            timeoutMs,
            onProgress: (progress) => updateFileState(index, { progress }),
            abortSignal: abortController.signal,
            tenantId,
          });

          // Success
          updateFileState(index, {
            status: 'success',
            progress: 100,
            url: fileUrl,
            error: undefined,
            retryCount: 0,
          });

          abortControllersRef.current.delete(index);
          onUploadComplete?.(fileUrl, file);
        } catch (error) {
          abortControllersRef.current.delete(index);

          // Handle abort
          if (error instanceof Error && error.message === 'Upload aborted') {
            updateFileState(index, { status: 'error', error: 'Upload cancelled' });
            return;
          }

          // Classify error
          const uploadError =
            (error as Error & { uploadError?: UploadError }).uploadError ||
            classifyError(error, (error as Error & { statusCode?: number }).statusCode);

          // Retry if possible
          if (uploadError.retryable && retryCount < maxRetries) {
            const delay = getRetryDelay(retryCount, retryDelayMs);
            onRetry?.(file, retryCount + 1, uploadError);

            updateFileState(index, {
              status: 'retrying',
              error: `${uploadError.message} Retrying in ${Math.round(delay / 1000)}s...`,
              retryCount: retryCount + 1,
            });

            await sleep(delay);
            return uploadFile(file, index, retryCount + 1);
          }

          // Final error
          const finalMessage =
            retryCount >= maxRetries
              ? `${uploadError.message} (max retries exceeded)`
              : uploadError.message;

          updateFileState(index, { status: 'error', error: finalMessage, retryCount });
          onUploadError?.(new Error(finalMessage), file);
        }
      },
      [
        getUploadUrl,
        maxRetries,
        onRetry,
        onUploadComplete,
        onUploadError,
        retryDelayMs,
        showDevResponse,
        timeoutMs,
        updateFileState,
      ]
    );

    // ========================================
    // File Handling
    // ========================================

    const getValidationErrorMessage = useCallback(
      (file: File): string | undefined => {
        // Check file size
        if (file.size > maxFileSize) {
          const customMessage = onValidationError?.({
            type: 'FILE_TOO_LARGE',
            file,
            maxFileSize,
          });
          return (
            customMessage ?? `File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`
          );
        }

        // Check file type
        const acceptedTypesList = accept.split(',').map((t) => t.trim().toLowerCase());
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

        if (!acceptedTypesList.includes(fileExtension)) {
          const customMessage = onValidationError?.({
            type: 'INVALID_FILE_TYPE',
            file,
            acceptedTypes: accept,
          });
          return customMessage ?? `File type ${fileExtension} is not supported`;
        }

        return undefined;
      },
      [accept, maxFileSize, onValidationError]
    );

    const handleFiles = useCallback(
      (fileList: FileList | null) => {
        if (!fileList || disabled) return;

        const newFiles: UploadedFile[] = Array.from(fileList).map((file) => {
          const validationError = getValidationErrorMessage(file);
          return {
            file,
            status: validationError ? ('error' as const) : ('idle' as const),
            progress: 0,
            error: validationError,
          };
        });

        setFiles((prev) => {
          const updated = multiple ? [...prev, ...newFiles] : newFiles;
          return updated;
        });

        // Start uploads
        const startIndex = multiple ? files.length : 0;
        newFiles.forEach((f, i) => {
          if (f.status === 'idle') {
            uploadFile(f.file, startIndex + i);
          }
        });
      },
      [disabled, files.length, getValidationErrorMessage, multiple, uploadFile]
    );

    const handleRemoveFile = useCallback(
      (index: number) => {
        // Abort ongoing upload
        const abortController = abortControllersRef.current.get(index);
        if (abortController) {
          abortController.abort();
          abortControllersRef.current.delete(index);
        }

        // Use functional update to get current file state and handle removal
        setFiles((prev) => {
          const fileToRemove = prev[index];
          const fileId = fileToRemove?.url;

          // Remove from dev response section if showDevResponse is enabled
          if (showDevResponse && fileId) {
            uploadedFileIdsRef.current = uploadedFileIdsRef.current.filter((id) => id !== fileId);
            setApiResponses((prevResponses) =>
              prevResponses.filter(
                (response) => (response as { fileId?: string }).fileId !== fileId
              )
            );
          }

          // Pass fileId for server-side deletion (url contains the fileId)
          onFileRemove?.(fileId);

          return prev.filter((_, i) => i !== index);
        });
      },
      [onFileRemove, showDevResponse]
    );

    const handleRetryFile = useCallback(
      (index: number) => {
        const fileToRetry = files[index];
        if (fileToRetry?.status === 'error') {
          uploadFile(fileToRetry.file, index, 0);
        }
      },
      [files, uploadFile]
    );

    // ========================================
    // Drag & Drop Handlers
    // ========================================

    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setIsDragging(true);
      },
      [disabled]
    );

    const handleDragLeave = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    }, []);

    const handleDragOver = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) setIsDragging(true);
      },
      [disabled]
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (!disabled) handleFiles(e.dataTransfer.files);
      },
      [disabled, handleFiles]
    );

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        handleFiles(e.target.files);
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
      [handleFiles]
    );

    // ========================================
    // Status Icon Renderer
    // ========================================

    const renderStatusIcon = (status: FileUploadStatus, index: number) => {
      switch (status) {
        case 'uploading':
        case 'retrying':
          return (
            <div className={styles.spinner} data-testid={`${dataTestId}-spinner`}>
              <Iconography category="global" name="refresh" theme={theme} color="white" />
            </div>
          );
        case 'success':
          return (
            <Iconography
              category="global"
              name="circle-checkmark-filled"
              theme={theme}
              color="success-1"
            />
          );
        case 'error':
          return (
            <>
              <Iconography category="global" name="warning" theme={theme} color="error-1" />
              <IconButton
                dataTestId={`${dataTestId}-retry-${index}`}
                buttonType="tertiary"
                size="small"
                theme={theme}
                handleClick={() => handleRetryFile(index)}
                className={styles.retryButton}
                aria-label="Retry upload"
                icon={{ category: 'global', name: 'refresh' }}
                gaComponent="file-upload-retry"
              />
            </>
          );
        default:
          return null;
      }
    };

    // ========================================
    // Render
    // ========================================

    const getValidationClass = () => {
      if (!validationResponse?.validationType) return undefined;
      const typeMap: Record<string, string> = {
        success: 'validationSuccess',
        warning: 'validationWarning',
        error: 'validationError',
      };
      return styles[typeMap[validationResponse.validationType]];
    };

    return (
      <Box
        ref={ref}
        className={classnames(styles.container, styles[theme], getValidationClass(), className)}
        data-testid={dataTestId}
        data-theme={theme}
      >
        {(label || helperText) && (
          <Box className={styles.labelWrapper}>
            {label && (
              <Typography
                variant="label"
                size="small"
                theme={theme}
                color="gray-3"
                className={styles.label}
                data-testid={`${dataTestId}-label`}
              >
                {label}
                {required && '*'}
              </Typography>
            )}
            {helperText && (
              <Typography
                variant="body"
                size="extraSmall"
                theme={theme}
                color="gray-5"
                data-testid={`${dataTestId}-helper-text`}
              >
                {helperText}
              </Typography>
            )}
          </Box>
        )}

        <div
          className={classnames(styles.dropzone, {
            [styles.dragging]: isDragging,
            [styles.disabled]: disabled,
          })}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid={`${dataTestId}-dropzone`}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleInputChange}
            className={styles.hiddenInput}
            disabled={disabled}
            data-testid={`${dataTestId}-input`}
          />
          <Typography variant="body" size="small" theme={theme} color="gray-4">
            {dropzoneText}
          </Typography>
        </div>

        {files.length > 0 && (
          <Box className={styles.fileList} data-testid={`${dataTestId}-file-list`}>
            {files.map((uploadedFile, index) => (
              <Box
                key={`${uploadedFile.file.name}-${index}`}
                className={classnames(styles.fileItem, {
                  [styles.error]: uploadedFile.status === 'error',
                })}
                data-testid={`${dataTestId}-file-${index}`}
              >
                <Box className={styles.fileInfo}>
                  <Iconography
                    category="global"
                    name="image"
                    theme={theme}
                    color="white"
                    className={styles.fileIcon}
                  />
                  <Box className={styles.fileDetails}>
                    <Typography
                      variant="body"
                      size="small"
                      theme={theme}
                      className={styles.fileName}
                    >
                      {uploadedFile.file.name}
                    </Typography>
                    {(uploadedFile.status === 'uploading' ||
                      uploadedFile.status === 'retrying') && (
                      <ProgressBar
                        dataTestId={`${dataTestId}-progress-${index}`}
                        value={uploadedFile.progress}
                        theme={theme}
                        size="small"
                        variant={uploadedFile.status === 'retrying' ? 'warning' : 'default'}
                        animated
                      />
                    )}
                    {uploadedFile.status === 'retrying' && uploadedFile.error && (
                      <Typography variant="body" size="extraSmall" theme={theme} color="warning-1">
                        {uploadedFile.error}
                      </Typography>
                    )}
                    {uploadedFile.status === 'error' && uploadedFile.error && (
                      <Typography variant="body" size="extraSmall" theme={theme} color="error-1">
                        {uploadedFile.error}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box className={styles.fileActions}>
                  {renderStatusIcon(uploadedFile.status, index)}
                  <IconButton
                    dataTestId={`${dataTestId}-remove-${index}`}
                    buttonType="tertiary"
                    size="small"
                    theme={theme}
                    handleClick={() => handleRemoveFile(index)}
                    className={styles.removeButton}
                    aria-label={`Remove ${uploadedFile.file.name}`}
                    icon={{ category: 'global', name: 'close' }}
                    gaComponent="file-upload-remove"
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Dev Response Section */}
        {showDevResponse && apiResponses.length > 0 && (
          <Box className={styles.devResponseSection} data-testid={`${dataTestId}-dev-response`}>
            <Box className={styles.devResponseHeader}>
              <Typography variant="body" size="large" theme={theme} color="black">
                📋 API Response (Dev Reference)
              </Typography>
              <button
                type="button"
                onClick={() => {
                  setApiResponses([]);
                  uploadedFileIdsRef.current = [];
                }}
                className={styles.clearButton}
                data-testid={`${dataTestId}-clear-responses`}
              >
                Clear All
              </button>
            </Box>

            <Box className={styles.devResponseContent}>
              <Typography variant="body" size="small" theme={theme} color="black">
                Use these file IDs when updating other endpoints:
              </Typography>
            </Box>

            <Box className={styles.fileIdList}>
              <Typography variant="body" size="small" theme={theme} color="success-1">
                File IDs: [{uploadedFileIdsRef.current.map((id) => `"${id}"`).join(', ')}]
              </Typography>
            </Box>

            {apiResponses.map((response, index) => (
              <Box
                key={`response-${index}`}
                className={styles.responseBlock}
                data-testid={`${dataTestId}-response-${index}`}
              >
                <Box className={styles.responseBlockHeader}>
                  <Typography variant="body" size="small" theme={theme} color="black">
                    Response #{index + 1}:{' '}
                    {(response as { fileNameOriginal?: string }).fileNameOriginal || 'File'}
                  </Typography>
                </Box>
                <pre className={styles.jsonResponse}>{JSON.stringify(response, null, 2)}</pre>
              </Box>
            ))}
          </Box>
        )}

        {showValidation && (
          <Box className={styles.validationMessageWrapper} role="alert" aria-live="assertive">
            {validationResponse?.message && (
              <Typography
                className={styles.validationMessage}
                variant="body"
                size="extraSmall"
                theme={theme}
                data-testid={`${dataTestId}-validation-message`}
              >
                {validationResponse.message}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

FileUpload.displayName = 'FileUpload';

export default FileUpload;
